import {
    Box,
    Paper,
    Typography,
    Chip,
} from "@mui/material";
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CalendarToday as CalendarIcon,
    Cake as CakeIcon,
    Wc as GenderIcon,
    Security as StatusIcon,
    Star as DefaultTrainerIcon,
    AdminPanelSettings as CreatorIcon,
    Fingerprint as IdentifierIcon
} from "@mui/icons-material";
import moment from "moment";
import {formatPhoneForDisplay} from "../shared/PhoneInput";
import {t as coachT} from "../dashboard/coach/coachTokens";

const TrainerProfileSection = ({trainer}) => {
    const getGenderText = (gender) => {
        switch (gender) {
            case "FEMALE":
                return "נקבה";
            case "MALE":
                return "זכר";
            default:
                return "אין מידע";
        }
    };

    const getCreatedByTypeText = (createdByType) => {
        switch (createdByType) {
            case "SELF":
                return "רישום עצמאי";
            case "SYSTEM_ADMIN":
                return "מנהל מערכת";
            case "ADMIN":
                return "מנהל חברה";
            case "TRAINER":
                return "מאמן";
            case "ARBOX":
                return "ARBOX";
            default:
                return "אין מידע";
        }
    };

    return (
        <Paper
            sx={{
                backgroundColor: coachT.white,
                border: `1px solid ${coachT.border}`,
                borderRadius: coachT.r,
                boxShadow: coachT.sh,
                overflow: "hidden",
                direction: "rtl"
            }}
            elevation={0}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 22px",
                    backgroundColor: coachT.bg,
                    borderBottom: `1px solid ${coachT.border}`
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <PersonIcon sx={{ color: coachT.t2, fontSize: 22 }} />
                    </Box>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "17px",
                            color: coachT.text,
                            letterSpacing: "-0.2px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        פרטים אישיים
                    </Typography>
                    {trainer.defaultTrainer && (
                        <Chip
                            label="מאמן ברירת מחדל"
                            icon={<DefaultTrainerIcon />}
                            size="small"
                            sx={{
                                backgroundColor: coachT.blueLight,
                                color: coachT.blueText,
                                "& .MuiChip-icon": {color: coachT.blueText},
                                fontWeight: 600,
                                fontSize: "11px",
                                height: "24px",
                                marginRight: "8px"
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{padding: "8px 22px 12px"}}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)",
                            lg: "repeat(4, 1fr)"
                        },
                        columnGap: "24px",
                    }}
                >
                    {/* Basic Information */}
                    <ProfileRow
                        icon={<PersonIcon />}
                        label="שם מלא"
                        value={trainer.name || "אין מידע"}
                    />

                    <ProfileRow
                        icon={<CakeIcon />}
                        label="גיל"
                        value={trainer.age || "אין מידע"}
                    />

                    <ProfileRow
                        icon={<GenderIcon />}
                        label="מגדר"
                        value={getGenderText(trainer.gender)}
                    />

                    <ProfileRow
                        icon={<StatusIcon />}
                        label="סטטוס"
                        value={
                            <Chip
                                label={trainer.enabled ? "פעיל" : "חסום"}
                                size="small"
                                sx={{
                                    backgroundColor: trainer.enabled ? coachT.greenLight : coachT.redLight,
                                    color: trainer.enabled ? coachT.greenText : coachT.redText,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: "20px"
                                }}
                            />
                        }
                    />

                    {/* Contact Information */}
                    <ProfileRow
                        icon={<EmailIcon />}
                        label="מייל"
                        value={trainer.email || "אין מידע"}
                    />

                    <ProfileRow
                        icon={<PhoneIcon />}
                        label="טלפון"
                        value={
                          <Typography sx={{ direction: "ltr", fontSize: "13px", fontWeight: 700, color: coachT.text }}>
                            {formatPhoneForDisplay(trainer.phoneNumber) || "אין מידע"}
                          </Typography>
                        }
                    />

                    {/* Date Information */}
                    <ProfileRow
                        icon={<CalendarIcon />}
                        label="תאריך יצירה"
                        value={trainer.createdDate ? moment(trainer.createdDate * 1000).format("DD/MM/YY") : "אין מידע"}
                    />

                    {/* Creator Information */}
                    <ProfileRow
                        icon={<CreatorIcon />}
                        label="נוצר על ידי"
                        value={getCreatedByTypeText(trainer.createdByType)}
                    />


                    <ProfileRow
                        icon={<IdentifierIcon />}
                        label="שם היוצר"
                        value={
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    color: coachT.text,
                                    lineHeight: "18px",
                                    fontFamily: "monospace",
                                    wordBreak: "break-all"
                                }}
                            >
                                {trainer.createdByIdentifier || "לא ידוע"}
                            </Typography>
                        }
                    />

                </Box>
            </Box>
        </Paper>
    );
};

const ProfileRow = ({icon, label, value}) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        padding: "8px 0", borderBottom: `1px solid ${coachT.borderLight}`, minHeight: 36,
    }}>
        <Box sx={{color: coachT.t3, display: "flex", alignItems: "center", flexShrink: 0, "& .MuiSvgIcon-root": {fontSize: "16px"}}}>
            {icon}
        </Box>
        <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: coachT.t2, flexShrink: 0}}>
            {label}
        </Typography>
        <Box sx={{flex: 1}}/>
        {typeof value === 'string' ? (
            <Typography sx={{fontSize: "13px", fontWeight: 700, color: coachT.text, textAlign: "left", wordBreak: "break-word"}}>
                {value}
            </Typography>
        ) : (
            value
        )}
    </Box>
);

export default TrainerProfileSection;